
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CurrencyInfo {
  code: string;
  symbol: string;
  price: string;
  buttonText: string;
  enabled: boolean;
}

interface Profile {
  country_code?: string;
  country_name?: string;
}

// Geolocation detection for non-logged in users
const detectUserLocation = async (): Promise<{ isIndian: boolean; countryCode?: string }> => {
  try {
    // Try IP-based geolocation first
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const response = await fetch('https://ipapi.co/json/', { 
      signal: controller.signal,
      headers: { 'Accept': 'application/json' }
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      return {
        isIndian: data.country_code === 'IN' || data.country?.toLowerCase() === 'india',
        countryCode: data.country_code
      };
    }
  } catch (error) {
    console.log('IP geolocation failed, using fallback');
  }
  
  // Fallback to timezone detection
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isIndianTimezone = timezone === 'Asia/Kolkata' || timezone === 'Asia/Calcutta';
    return { isIndian: isIndianTimezone };
  } catch (error) {
    console.log('Timezone detection failed');
  }
  
  // Final fallback - default to USD for non-logged users
  return { isIndian: false };
};

export const useCurrency = () => {
  const [currency, setCurrency] = useState<CurrencyInfo>({
    code: 'USD',
    symbol: '$',
    price: '8',
    buttonText: 'Get Premium Access - $8',
    enabled: false
  });
  const [loading, setLoading] = useState(true);

  const fetchCurrencySettings = async () => {
    try {
      setLoading(true);
      
      let isIndianUser = false;
      let userProfile: Profile | null = null;
      
      // Try to get current user profile
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase
            .from('profiles')
            .select('country_code, country_name')
            .eq('id', user.id)
            .maybeSingle();
          userProfile = data;
        }
      } catch (error) {
        console.log('User not logged in or profile not found');
      }
      
      if (userProfile) {
        // For logged in users - prioritize country_code for reliable detection
        // Check country_code first, then fallback to country_name
        isIndianUser = userProfile.country_code === '+91' || 
                      (userProfile.country_code === 'IN') ||
                      (!userProfile.country_code && userProfile.country_name?.toLowerCase().includes('india'));
      } else {
        // For non-logged in users - detect location via IP
        const location = await detectUserLocation();
        isIndianUser = location.isIndian;
      }
      
      console.log('Currency detection:', { userProfile, isIndianUser });
      
      // Get appropriate price settings
      const priceKey = isIndianUser ? 'premium_price_inr' : 'premium_price_usd';
      const enabledKey = isIndianUser ? 'currency_enabled_inr' : 'currency_enabled_usd';
      
      const { data: settings, error } = await supabase
        .from('printables_settings')
        .select('setting_key, setting_value')
        .in('setting_key', [priceKey, enabledKey]);

      if (error) throw error;

      const priceData = settings?.find(s => s.setting_key === priceKey);
      const enabledData = settings?.find(s => s.setting_key === enabledKey);
      
      const price = priceData?.setting_value || (isIndianUser ? '149' : '8');
      const enabled = enabledData?.setting_value === 'true';
      
      const currencyInfo: CurrencyInfo = isIndianUser ? {
          code: 'INR',
          symbol: '₹',
          price: price,
          buttonText: `Unlock Learning - ₹${price}`,
          enabled
      } : {
          code: 'USD',
          symbol: '$',
          price: price,
          buttonText: `Unlock Learning - $${price}`,
          enabled
      };

      console.log('Currency info set:', currencyInfo);
      setCurrency(currencyInfo);
    } catch (error) {
      console.error('Error fetching currency settings:', error);
      
      // Fallback based on user location - default to USD for errors
      const isIndianUser = false;
      
      setCurrency(isIndianUser ? {
        code: 'INR',
        symbol: '₹',
        price: '149',
        buttonText: 'Get Premium Access - ₹149',
        enabled: true
      } : {
        code: 'USD',
        symbol: '$',
        price: '8',
        buttonText: 'Get Premium Access - $8',
        enabled: false // Disabled until Razorpay International is approved
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencySettings();
  }, []);

  return { currency, loading, refreshCurrency: fetchCurrencySettings };
};
